import { useState } from 'react';
import { Modal } from '@/components/primitives/Modal';
import { Button } from '@/components/primitives/Button';
import { Icon } from '@/components/primitives/Icon';

interface UploadAssetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (file: File, name: string) => void;
  /** When true, upload is globally busy */
  isBusy?: boolean;
}

export function UploadAssetModal({ isOpen, onClose, onConfirm, isBusy }: UploadAssetModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [tab, setTab] = useState<'device' | 'url' | 'drive'>('device');
  const [error, setError] = useState('');
  const [webUrl, setWebUrl] = useState('');
  const [webName, setWebName] = useState('');
  const [webLoading, setWebLoading] = useState(false);

  function nameFromUrl(url: string): string {
    try {
      const path = new URL(url).pathname;
      return path.split('/').filter(Boolean).pop() ?? 'asset';
    } catch {
      return 'asset';
    }
  }

  function isSupportedMedia(blob: Blob, contentType: string): boolean {
    const mime = (blob.type || contentType).split(';')[0].toLowerCase();
    return mime.startsWith('image/') || mime.startsWith('video/');
  }

  const resetForm = () => {
    setFile(null);
    setName('');
    setTab('device');
    setError('');
    setWebUrl('');
    setWebName('');
  };

  const handleClose = () => {
    if (!isBusy && !webLoading) {
      resetForm();
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (tab === 'device') {
      if (!file) {
        setError('Select a file to upload.');
        return;
      }
      onConfirm(file, name.trim());
      setFile(null);
      setName('');
      setError('');
      return;
    }
    if (tab === 'url') {
      const url = webUrl.trim();
      if (!url) {
        setError('Enter a URL to fetch.');
        return;
      }
      setWebLoading(true);
      setError('');
      try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = res.headers.get('content-type') ?? '';
        const blob = await res.blob();
        if (!isSupportedMedia(blob, ct)) {
          throw new Error('URL must point to an image or video.');
        }
        const filename = webName.trim() || nameFromUrl(url);
        const fileFromUrl = new File([blob], filename, {
          type: blob.type || ct.split(';')[0],
        });
        onConfirm(fileFromUrl, filename);
        resetForm();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to fetch URL.');
      } finally {
        setWebLoading(false);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload Asset"
      subtitle="Select a file from your computer to add to your library."
      width="max-w-md"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button variant="ghost" onClick={handleClose} disabled={isBusy}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={
              isBusy ||
              webLoading ||
              (tab === 'device' && !file) ||
              (tab === 'url' && webUrl.trim() === '') ||
              tab === 'drive'
            }
          >
            {isBusy ? 'Uploading...' : webLoading ? 'Fetching...' : 'Confirm'}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="flex p-1 bg-surface-container-low rounded-xl">
          <button
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${tab === 'device' ? 'bg-surface-container-lowest shadow text-primary' : 'text-on-surface-variant hover:text-on-surface'}`}
            onClick={() => {
              setTab('device');
              setError('');
            }}
          >
            From Device
          </button>
          <button
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${tab === 'url' ? 'bg-surface-container-lowest shadow text-primary' : 'text-on-surface-variant hover:text-on-surface'}`}
            onClick={() => {
              setTab('url');
              setError('');
            }}
          >
            Web URL
          </button>
          <button
            className={`flex-1 py-2 text-sm font-bold rounded-lg transition-colors ${tab === 'drive' ? 'bg-surface-container-lowest shadow text-primary' : 'text-on-surface-variant hover:text-on-surface'}`}
            onClick={() => {
              setTab('drive');
              setError('');
            }}
          >
            Drive
          </button>
        </div>

        {tab === 'device' && (
          <div>
            <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-2">
              File
            </label>
            <div className="relative border-2 border-dashed border-outline-variant/30 rounded-xl p-8 hover:bg-surface-container-low transition-colors text-center">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm,video/quicktime"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setFile(f);
                    if (!name) setName(f.name);
                    setError('');
                  }
                }}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                disabled={isBusy}
              />
              {file ? (
                <div className="flex flex-col items-center">
                  <Icon name="check_circle" className="text-4xl text-primary mb-2" />
                  <p className="text-sm font-bold text-on-surface truncate max-w-full">
                    {file.name}
                  </p>
                  <p className="text-xs text-on-surface-variant mt-1">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Icon name="upload_file" className="text-4xl text-on-surface-variant/50 mb-2" />
                  <p className="text-sm font-bold text-on-surface">Click or drag file to upload</p>
                  <p className="text-xs text-on-surface-variant mt-1">
                    Supports Images/Videos up to 200MB
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'url' && (
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-2">
                URL
              </label>
              <input
                type="url"
                required
                value={webUrl}
                onChange={(e) => setWebUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-4 py-3 bg-surface-container-low text-sm text-on-surface border border-outline-variant/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all font-medium"
                disabled={isBusy || webLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-2">
                Display Name (Optional)
              </label>
              <input
                className="w-full px-4 py-3 bg-surface-container-low text-sm text-on-surface border border-outline-variant/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all font-medium"
                placeholder="e.g. Summer Collection Video"
                value={webName}
                onChange={(e) => setWebName(e.target.value)}
                disabled={isBusy || webLoading}
              />
            </div>
            <p className="text-xs text-on-surface-variant">
              We will fetch the URL and save the media to your asset library.
            </p>
          </div>
        )}

        {tab === 'drive' && (
          <div className="border-2 border-dashed border-outline-variant/30 rounded-xl p-8 flex flex-col items-center justify-center text-center">
            <Icon name="construction" className="text-4xl text-on-surface-variant/50 mb-2" />
            <p className="text-sm font-bold text-on-surface">Coming Soon</p>
            <p className="text-xs text-on-surface-variant mt-1">
              This integration is currently under development.
            </p>
          </div>
        )}

        {tab === 'device' && (
          <div>
            <label className="block text-xs font-semibold text-on-surface-variant uppercase tracking-wider mb-2">
              Display Name (Optional)
            </label>
            <input
              className="w-full px-4 py-3 bg-surface-container-low text-sm text-on-surface border border-outline-variant/20 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all font-medium"
              placeholder="e.g. Summer Collection Video"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isBusy || webLoading}
            />
          </div>
        )}
        {error && <p className="text-xs text-error font-semibold">{error}</p>}
      </div>
    </Modal>
  );
}
